import apiClient, { handleApiError } from '../../utils/apiClient';
import type { TreatmentCategory } from '../../domain/reservationSlots';

/**
 * APIレスポンスの共通型
 *
 * @template T - レスポンスデータの型
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * テンプレートAPIレスポンス項目（snake_case）
 */
export interface TemplateApiItem {
  id: string;
  name: string;
  interval_minutes: number;
  pattern_type: 'single' | 'alternate';
  slots_per_interval: number;
  slots_a: TreatmentCategory[];
  slots_b: TreatmentCategory[] | null;
}

/**
 * テンプレート作成・更新リクエストボディ（snake_case）
 */
export interface TemplateApiInput {
  name: string;
  interval_minutes: number;
  pattern_type: 'single' | 'alternate';
  slots_per_interval: number;
  slots_a: TreatmentCategory[];
  slots_b: TreatmentCategory[] | null;
}

/**
 * 基本設定APIレスポンス（snake_case）
 */
export interface SettingsApiResponse {
  default_template_id: string | null;
  overrides: Array<{
    doctor_id: number;
    doctor_name: string;
    template_id: string;
  }>;
}

/**
 * 基本設定更新リクエストボディ（snake_case）
 */
export interface SettingsApiInput {
  default_template_id: string | null;
  overrides: Array<{
    doctor_id: number;
    template_id: string;
  }>;
}

/**
 * 生成対象シフトAPIレスポンス項目（snake_case）
 */
export interface TargetShiftApiItem {
  doctor_shift_id: number;
  date: string;
  start_time: string;
  end_time: string;
  doctor_id: number;
  doctor_name: string;
  source_template_id: string | null;
}

/**
 * 一括生成リクエストボディ（snake_case）
 */
export interface GenerateApiInput {
  year: number;
  month: number;
  assignments: Array<{
    doctor_shift_id: number;
    template_id: string;
  }>;
}

/**
 * 一括生成APIレスポンス（snake_case）
 */
export interface GenerateApiResponse {
  created_count: number;
  category_counts: Partial<Record<TreatmentCategory, number>>;
}

/**
 * 月別サマリAPIレスポンス項目（snake_case）
 */
export interface SummaryApiItem {
  year: number;
  month: number;
  total_slots: number;
  category_counts: Partial<Record<TreatmentCategory, number>>;
}

// APIエンドポイントのベースパス
// ※バックエンド側の実装パスと一致させること
const TEMPLATE_BASE = '/api/reservation-slot-templates';
const SETTINGS_BASE = '/api/reservation-slot-settings';
const SLOTS_BASE = '/api/reservation-slots';

/**
 * 解放枠（予約枠）API クライアント
 *
 * @description
 * 解放枠管理に関するAPIエンドポイントを呼び出す。
 * エラーハンドリングを統一し、日本語エラーメッセージを提供する。
 * レスポンスは snake_case のまま返し、camelCase変換はRepository実装で行う。
 */
export const reservationSlotApi = {
  /**
   * テンプレート一覧を取得する
   */
  getTemplates: async (): Promise<TemplateApiItem[]> => {
    try {
      const response = await apiClient.get<ApiResponse<TemplateApiItem[]>>(TEMPLATE_BASE);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'テンプレート一覧の取得に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * テンプレートを新規作成する
   */
  createTemplate: async (input: TemplateApiInput): Promise<TemplateApiItem> => {
    try {
      const response = await apiClient.post<ApiResponse<TemplateApiItem>>(TEMPLATE_BASE, input);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'テンプレートの作成に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * テンプレートを更新する
   */
  updateTemplate: async (id: string, input: TemplateApiInput): Promise<TemplateApiItem> => {
    try {
      const response = await apiClient.put<ApiResponse<TemplateApiItem>>(`${TEMPLATE_BASE}/${id}`, input);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'テンプレートの更新に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * テンプレートを削除する
   */
  deleteTemplate: async (id: string): Promise<void> => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(`${TEMPLATE_BASE}/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'テンプレートの削除に失敗しました');
      }
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * 基本設定を取得する
   */
  getSettings: async (): Promise<SettingsApiResponse> => {
    try {
      const response = await apiClient.get<ApiResponse<SettingsApiResponse>>(SETTINGS_BASE);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '基本設定の取得に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * 基本設定を更新する
   */
  updateSettings: async (input: SettingsApiInput): Promise<SettingsApiResponse> => {
    try {
      const response = await apiClient.put<ApiResponse<SettingsApiResponse>>(SETTINGS_BASE, input);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '基本設定の更新に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * 生成対象シフトを取得する
   */
  getTargetShifts: async (year: number, month: number): Promise<TargetShiftApiItem[]> => {
    try {
      const response = await apiClient.get<ApiResponse<TargetShiftApiItem[]>>(
        `${SLOTS_BASE}/target-shifts`,
        { params: { year, month } }
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '生成対象シフトの取得に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * 解放枠を一括生成する
   */
  generate: async (input: GenerateApiInput): Promise<GenerateApiResponse> => {
    try {
      const response = await apiClient.post<ApiResponse<GenerateApiResponse>>(
        `${SLOTS_BASE}/generate`,
        input
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '解放枠の生成に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  /**
   * 月別サマリ一覧を取得する
   */
  getSummary: async (): Promise<SummaryApiItem[]> => {
    try {
      const response = await apiClient.get<ApiResponse<SummaryApiItem[]>>(`${SLOTS_BASE}/summary`);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'サマリの取得に失敗しました');
      }
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};
